"use client";

import { Cpu, Crosshair, Layers, ScanLine, Shield, Sparkles, Target, Wrench } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InteligenciaEnSeguridad() {
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const subParagraph = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const footer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const headingWords = ["La", "precisión", "detrás", "de", "su"];
  const features = [{
    title: "Monitoreo C4 Avanzado",
    description: "Centro de comando operativo 24/7 con tecnología de respuesta inmediata y coordinación directa con autoridades federales.",
    spec: "Respuesta < 3 min · Enlace Directo",
    icon: Crosshair,
    span: "lg:col-span-5",
    size: "large"
  }, {
    title: "Protocolos de Reacción",
    description: "Sistemas de seguridad escalonada diseñados bajo estándares internacionales de protección patrimonial y ejecutiva.",
    spec: "Certificación ISO-9001",
    icon: Layers,
    span: "lg:col-span-4",
    size: "medium"
  }, {
    title: "Análisis de Riesgos",
    description: "Evaluación técnica de vulnerabilidades mediante escaneo perimetral y digital.",
    spec: "Precisión Estratégica",
    icon: ScanLine,
    span: "lg:col-span-3",
    size: "compact"
  }, {
    title: "Rastreo Satelital GPS",
    description: "Geocercas inteligentes y seguimiento vehicular en tiempo real para logística segura.",
    spec: "Cobertura Nacional",
    icon: Target,
    span: "lg:col-span-3",
    size: "compact"
  }, {
    title: "Inteligencia Operativa",
    description: "Uso de algoritmos de videovigilancia para la detección temprana de anomalías y prevención de incidentes.",
    spec: "IA de Reconocimiento",
    icon: Cpu,
    span: "lg:col-span-4",
    size: "medium"
  }, {
    title: "Control de Confianza",
    description: "Personal rigurosamente evaluado con pruebas psicométricas y de entorno social.",
    spec: "Filtros de Selección",
    icon: Shield,
    span: "lg:col-span-2",
    size: "compact"
  }, {
    title: "Mantenimiento Técnico",
    description: "Soporte preventivo constante a toda nuestra infraestructura tecnológica.",
    spec: "Disponibilidad 99.9%",
    icon: Wrench,
    span: "lg:col-span-2",
    size: "compact"
  }];
  return <section data-section-id="5137" id="inteligencia-en-seguridad" className="bg-background text-foreground py-24 md:py-36 lg:py-44 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="grid grid-cols-12 gap-x-6 gap-y-10 mb-16 md:mb-24">
          <div ref={eyebrow.ref} className={`col-span-12 lg:col-start-2 lg:col-span-10 transition-all duration-700 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="flex items-center gap-3 font-mono text-xs tracking-[0.15em] uppercase text-[var(--chart-2)]">
              <span className="inline-block w-8 h-px bg-[var(--chart-2)]" />
              <span>ESTÁNDARES · DISCIPLINA</span>
            </div>
          </div>

          <h2 ref={heading.ref} className="col-span-12 lg:col-start-2 lg:col-span-8 font-default text-4xl sm:text-5xl md:text-6xl lg:text-[64px] leading-[1.1] tracking-tighter text-foreground">
            {headingWords.map((word, i) => <span key={i} data-index={i} className="inline-block overflow-hidden align-bottom mr-[0.25em]">
                <span className={`inline-block transition-all ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full"}`} style={{
              transitionDuration: "800ms",
              transitionDelay: `${i * 90}ms`
            }}>
                  {word}
                </span>
              </span>)}
            <span className="inline-block overflow-hidden align-bottom">
              <span className={`inline-block italic font-light text-[var(--chart-2)] transition-all ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full"}`} style={{
              transitionDuration: "800ms",
              transitionDelay: `${headingWords.length * 90}ms`
            }}>
                seguridad.
              </span>
            </span>
          </h2>

          <p ref={subParagraph.ref} className={`col-span-12 lg:col-start-2 lg:col-span-6 text-lg text-muted-foreground leading-relaxed font-default transition-all duration-700 delay-300 ease-out ${subParagraph.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            En Grupo Poseidón, cada estrategia de protección se fundamenta en un ecosistema deliberado de tecnología de vanguardia, procesos certificados y un compromiso inquebrantable con la integridad.
          </p>
        </div>

        {}
        <div ref={grid.ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          const isLarge = feature.size === "large";
          const isMedium = feature.size === "medium";
          return <div key={i} data-index={i} className={`group relative ${feature.span} rounded-md border border-border bg-card p-6 md:p-7 lg:p-8 transition-all duration-500 ease-out hover:border-[var(--chart-2)] hover:bg-accent/5 ${isLarge ? "min-h-[280px] md:min-h-[340px]" : isMedium ? "min-h-[240px] md:min-h-[280px]" : "min-h-[200px] md:min-h-[240px]"} ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: grid.isVisible ? `${i * 80}ms` : "0ms",
            transitionDuration: "700ms"
          }}>
                {}
                <div className="absolute top-5 right-5 flex items-center gap-2">
                  <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-md bg-[var(--chart-2)]" />
                </div>

                {}
                <div className={`${isLarge ? "w-20 h-20" : "w-12 h-12"} flex items-center justify-center rounded-md border border-border bg-background/40 text-foreground/70 transition-colors duration-500 group-hover:text-[var(--chart-2)] group-hover:border-[var(--chart-2)]/40 mb-6`}>
                  <Icon className={isLarge ? "w-9 h-9" : "w-5 h-5"} strokeWidth={1.25} />
                </div>

                {}
                <div className="flex flex-col">
                  <h3 className={`font-default font-semibold ${isLarge ? "text-2xl md:text-[28px]" : isMedium ? "text-xl md:text-2xl" : "text-lg md:text-xl"} leading-tight text-foreground transition-colors duration-500 group-hover:text-[var(--chart-2)] mb-3`}>
                    {feature.title}
                  </h3>

                  <p className={`text-sm text-muted-foreground leading-relaxed mb-5 ${!isLarge && !isMedium ? "line-clamp-3" : ""}`}>
                    {feature.description}
                  </p>

                  <div className="mt-auto pt-4 border-t border-border">
                    <span className="font-mono text-[11px] tracking-wider uppercase text-muted-foreground">
                      {feature.spec}
                    </span>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {}
        <div ref={footer.ref} className={`mt-20 md:mt-28 flex items-center justify-center transition-all duration-700 ease-out ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="flex items-center gap-3 text-muted-foreground">
            <Sparkles className="w-3.5 h-3.5 text-[var(--chart-2)]" strokeWidth={1.5} />
            <p className="font-mono text-xs sm:text-sm tracking-[0.15em] uppercase text-center">
              <span>Equipamiento de Élite</span>
              <span className="mx-3 text-[var(--chart-2)]">·</span>
              <span>Procesos Certificados</span>
              <span className="mx-3 text-[var(--chart-2)]">·</span>
              <span>Protección Absoluta</span>
            </p>
            <Sparkles className="w-3.5 h-3.5 text-[var(--chart-2)]" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    </section>;
}